import { useState } from "react";
import { Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { isValidEmail } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import {
  isStrongPassword,
  MAX_PASSWORD_LENGTH,
  PASSWORD_REQUIREMENT_TEXT,
} from "@/lib/passwordPolicy";
import { ArrowLeft, Mail, CheckCircle, Eye, EyeOff, AlertCircle } from "lucide-react";
import SettingsToggle from "@/components/SettingsToggle";
import BrandLogo from "@/components/BrandLogo";
import { PasswordRequirements } from "@/components/PasswordRequirements";

export default function ForgotPassword() {
  // If the user arrived from the email link, the token is in the URL.
  const tokenFromUrl = new URLSearchParams(window.location.search).get("token") || "";
  const emailFromUrl = new URLSearchParams(window.location.search).get("email") || "";
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [adminMfaCode, setAdminMfaCode] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // "email" → request step; "email-sent" → check inbox; "reset" → set new password; "done" → success
  const [step, setStep] = useState<"email" | "email-sent" | "reset" | "done">(
    tokenFromUrl ? "reset" : "email"
  );
  const { toast } = useToast();
  const newPasswordIsStrong = isStrongPassword(newPassword);

  const requestResetMutation = useMutation({
    mutationFn: async (data: { email: string }) => {
      const response = await apiRequest("POST", "/api/auth/forgot-password", data);
      return response.json();
    },
    onSuccess: () => {
      setStep("email-sent");
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: async (data: { token: string; newPassword: string; adminMfaCode: string }) => {
      const response = await apiRequest("POST", "/api/auth/reset-password", data);
      return response.json();
    },
    onSuccess: () => {
      setStep("done");
      toast({
        title: "Password Reset!",
        description: "Your password has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Reset Failed",
        description: error.message || "Could not reset password. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleRequestReset = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({ title: "Error", description: "Please enter your email", variant: "destructive" });
      return;
    }
    if (!isValidEmail(email)) {
      toast({ title: "Invalid email", description: "Please enter a valid email address (e.g. name@example.com).", variant: "destructive" });
      return;
    }
    requestResetMutation.mutate({ email });
  };

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast({ title: "Error", description: "Please fill in all fields", variant: "destructive" });
      return;
    }
    if (!newPasswordIsStrong) {
      toast({ title: "Choose a stronger password", description: PASSWORD_REQUIREMENT_TEXT, variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "Passwords don't match", variant: "destructive" });
      return;
    }
    resetPasswordMutation.mutate({ token: tokenFromUrl, newPassword, adminMfaCode });
  };

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center py-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <Link href="/login">
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full" aria-label="Back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <SettingsToggle />
        </div>
        <div className="text-center mb-8">
          <div className="animate-float mb-4">
            <BrandLogo width={190} />
          </div>
          <p className="text-muted-foreground text-sm">
            Reset your cosmic credentials
          </p>
        </div>

        <Card className="cosmic-card border-0 shadow-lg overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
          <CardHeader className="pb-4">
            <CardTitle className="text-center text-lg font-medium text-foreground">
              {step === "email" && "Forgot Password"}
              {step === "email-sent" && "Check Your Email"}
              {step === "reset" && "Reset Password"}
              {step === "done" && "All Set!"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === "email" && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Enter your email and we'll send you a reset link.
                </p>
                <div className="space-y-1">
                  <Label htmlFor="email" className="text-sm font-medium text-foreground">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={requestResetMutation.isPending}
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                >
                  {requestResetMutation.isPending ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>
            )}

            {step === "email-sent" && (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-violet-500/10 flex items-center justify-center">
                  <Mail className="h-8 w-8 text-violet-500" />
                </div>
                <p className="text-foreground font-medium mb-2">Reset link sent!</p>
                <p className="text-sm text-muted-foreground mb-6">
                  If an account exists for that email, you'll receive a password reset link shortly. Click the link within five minutes to set a new password.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setStep("email")}
                >
                  Try a different email
                </Button>
              </div>
            )}

            {step === "reset" && !tokenFromUrl && (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-amber-500" />
                </div>
                <p className="text-foreground font-medium mb-2">Invalid or expired link</p>
                <p className="text-sm text-muted-foreground mb-6">
                  This reset link is missing or has expired. Please request a new one.
                </p>
                <Button
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                  onClick={() => setStep("email")}
                >
                  Request a new link
                </Button>
              </div>
            )}

            {step === "reset" && tokenFromUrl && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Choose a new password for your account.
                </p>
                {/* Hidden username field so password managers pair the new
                    password with the account email — without it they save the
                    URL reset token as the "username" and later autofill it
                    into the Sign In email box. */}
                <input
                  type="email"
                  name="username"
                  autoComplete="username"
                  value={emailFromUrl}
                  readOnly
                  aria-hidden="true"
                  tabIndex={-1}
                  className="sr-only"
                />
                <div className="space-y-1">
                  <Label htmlFor="adminMfaCode" className="text-sm font-medium text-foreground">
                    Authenticator or recovery code (administrators)
                  </Label>
                  <Input
                    id="adminMfaCode"
                    type="text"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    value={adminMfaCode}
                    onChange={(event) => setAdminMfaCode(event.target.value)}
                    maxLength={64}
                    placeholder="Enter a current code if this is an admin account"
                  />
                  <p className="text-xs text-muted-foreground">
                    Administrator password resets need a current authenticator code or an unused recovery code.
                  </p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="newPassword" className="text-sm font-medium text-foreground">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      maxLength={MAX_PASSWORD_LENGTH}
                      aria-describedby="password-requirements"
                      aria-invalid={newPassword.length > 0 && !newPasswordIsStrong}
                      placeholder="Create a strong password"
                      required
                      className={newPassword.length > 0 && !newPasswordIsStrong ? "pr-10 border-destructive focus-visible:ring-destructive" : "pr-10"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((s) => !s)}
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="button-toggle-new-password"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="mt-2">
                    <PasswordRequirements password={newPassword} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
                    Confirm Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      maxLength={MAX_PASSWORD_LENGTH}
                      placeholder="Confirm your password"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((s) => !s)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      data-testid="button-toggle-confirm-password"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={resetPasswordMutation.isPending}
                  className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                >
                  {resetPasswordMutation.isPending ? "Resetting..." : "Reset Password"}
                </Button>
                {resetPasswordMutation.isError && (
                  <div className="text-center mt-2">
                    <button
                      type="button"
                      className="text-sm text-violet-600 hover:text-violet-700 dark:text-violet-400"
                      onClick={() => setStep("email")}
                    >
                      Request a new link
                    </button>
                  </div>
                )}
              </form>
            )}

            {step === "done" && (
              <div className="text-center py-4">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-500" />
                </div>
                <p className="text-foreground font-medium mb-2">Password reset successfully!</p>
                <p className="text-sm text-muted-foreground mb-6">You can now sign in with your new password.</p>
                <Link href="/login">
                  <Button className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white">
                    Sign In Now
                  </Button>
                </Link>
              </div>
            )}

            <div className="text-center mt-6">
              <Link href="/login">
                <span className="text-sm text-muted-foreground hover:text-primary cursor-pointer inline-flex items-center gap-1">
                  <ArrowLeft className="h-3 w-3" />
                  Back to Sign In
                </span>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
